import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Heart, Users, Globe, Target, Shield, Handshake, MapPin, Euro, HandHeart, CheckCircle } from 'lucide-react';

const About = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentPersonSlide, setCurrentPersonSlide] = useState(0);
  const [currentSchoolSlide, setCurrentSchoolSlide] = useState(0);

  // Team members
  const teamMembers = [
    {
      name: "Stefan",
      image: "/images/stefan-updated.png",
      alt: "Stefan - Vorstandsmitglied"
    },
    {
      name: "Thomas",
      image: "https://customer-assets.emergentagent.com/job_a34eb1ed-43a6-4ee4-a791-0955c92643a8/artifacts/kexsi7jw_2.png",
      alt: "Thomas - Vorstandsmitglied"
    },
    {
      name: "Daniel",
      image: "https://customer-assets.emergentagent.com/job_a34eb1ed-43a6-4ee4-a791-0955c92643a8/artifacts/vb0xk9ev_3.png",
      alt: "Daniel - Vorstandsmitglied"
    },
    {
      name: "Nadja",
      image: "/images/nadja-new.png",
      alt: "Nadja - Vorstandsmitglied"
    }
  ];

  // Gaza images from Imgur
  const gazaImages = [
    {
      src: "https://i.imgur.com/g0cPIxY.jpg",
      alt: "Gaza - Kinder und Hoffnung",
      description: "Seit über zwei Jahren stehen Kinder für eine Mahlzeit an."
    },
    {
      src: "https://i.imgur.com/NNCetKb.jpg", 
      alt: "Gaza - Leben im Krieg",
      description: "Straßen sind zu Trümmerfeldern geworden – Kinder mittendrin."
    },
    {
      src: "https://i.imgur.com/PkJ3G4R.jpg",
      alt: "Gaza - Zerstörung und Mut",
      description: "Ein Kind sitzt dort, wo einst sein Zuhause stand."
    },
    {
      src: "/images/gaza-destruction-new.jpg",
      alt: "Gaza - Alltag im Ausnahmezustand",
      description: "Ein ganzes Wohnviertel: nach monatelangen Angriffen in Trümmern."
    },
    {
      src: "/images/gaza-people-lost-everything.jpg",
      alt: "Gaza - Menschen haben alles verloren",
      description: "Zwischen zerstörten Häusern und Autos suchen Menschen nach Nahrung."
    },
    {
      src: "/images/gaza-destruction-rubble.jpg",
      alt: "Gaza - Zerstörung und Trümmer",
      description: "Nach der Zerstörung beginnt jedes Mal der mühsame Wiederaufbau."
    },
    {
      src: "https://customer-assets.emergentagent.com/job_hayforhumanity/artifacts/vosjfvo3_mohammed-ibrahim-703Sobdug5o-unsplash.jpg",
      alt: "Gaza - Wiederaufbau nach der Zerstörung",
      description: "Kindheit zwischen Trümmern und Wäscheleinen – Alltag im Krieg."
    }
  ];

  // School reconstruction project images
  const schoolImages = [
    {
      src: "/images/school-1.jpg",
      alt: "Schule nach Bombenangriff",
      description: "Die Schule wurde 2025 bei einem Bombenangriff zerstört."
    },
    {
      src: "/images/school-2.jpg",
      alt: "Schule vor der Zerstörung",
      description: "Ein Alltag geprägt von Sicherheit und Lernen war nicht mehr vorstellbar."
    },
    {
      src: "/images/school-3.jpg",
      alt: "Wiederaufbau der Schule",
      description: "Mit unserer Unterstützung wird die Schule wieder aufgebaut."
    },
    {
      src: "/images/school-4.jpg",
      alt: "Bauarbeiten an der Schule",
      description: "Unsere Kontakte vor Ort arbeiten mit Ausdauer und handwerklichem Geschick unter schwierigen Bedingungen."
    },
    {
      src: "/images/school-5.jpg",
      alt: "Kinder in der Schule",
      description: "Kinder kommen wieder in die Schule."
    },
    {
      src: "/images/school-6.jpg",
      alt: "Hoffnung und Perspektive",
      description: "Ein Stück Alltag, Hoffnung und Perspektive sind zurück."
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % gazaImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + gazaImages.length) % gazaImages.length);
  };

  const nextPersonSlide = () => {
    setCurrentPersonSlide((prev) => (prev + 1) % teamMembers.length);
  };

  const prevPersonSlide = () => {
    setCurrentPersonSlide((prev) => (prev - 1 + teamMembers.length) % teamMembers.length);
  };

  const nextSchoolSlide = () => {
    setCurrentSchoolSlide((prev) => (prev + 1) % schoolImages.length);
  };

  const prevSchoolSlide = () => {
    setCurrentSchoolSlide((prev) => (prev - 1 + schoolImages.length) % schoolImages.length);
  };

  // Auto-advance slider
  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, []);

  // Auto-advance person slider
  useEffect(() => {
    const personTimer = setInterval(nextPersonSlide, 4000);
    return () => clearInterval(personTimer);
  }, []);

  // Auto-advance school slider
  useEffect(() => {
    const schoolTimer = setInterval(nextSchoolSlide, 5000);
    return () => clearInterval(schoolTimer);
  }, []);

  const values = [
    {
      icon: Heart,
      title: "Menschlichkeit",
      description: "Uns treibt pure Menschlichkeit an,<br />keine politische Agenda."
    },
    {
      icon: Shield,
      title: "Transparenz",
      description: "Jede Spende wird dokumentiert. Wir legen regelmäßig Rechenschaft ab."
    },
    {
      icon: Handshake,
      title: "Direkte Hilfe",
      description: "Schnelle Hilfe für Menschen in akuten Notlagen. Unbürokratisch und direkt."
    }
  ];

  const achievements = [
    {
      icon: Euro,
      number: "30.000+",
      text: "Euro bereits gesammelt und direkt vor Ort eingesetzt.",
      link: "https://gofundme.com/f/soforthilfe-fuer-gaza" // GoFundMe Link
    },
    {
      icon: Users,
      number: "200+",
      text: "Familien in Rafah, Khan Younis und Gaza-Stadt unterstützt."
    },
    {
      icon: HandHeart,
      number: "100 %",
      text: "Transparenz durch Dokumentation jeder Hilfsmaßnahme."
    }
  ];

  const helpMeasures = [
    "Lebensmittel für Familien in Rafah, Khan Younis und Gaza-Stadt.",
    "Medikamente für chronisch Kranke.",
    "Zelte und Hygieneartikel für Binnenvertriebene.",
    "Gezielte Einzelfallhilfe (z. B. für medizinische Notfälle und Prothesen)."
  ];

  return (
    <section id="about" className="py-20 bg-cream">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header mit Logo - Desktop: Text links, Logo rechts */}
          <motion.div
            className="grid lg:grid-cols-2 gap-12 items-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            {/* Text Section - Links */}
            <div className="text-center lg:text-left">
              <h2 className="text-5xl md:text-6xl font-bold text-charcoal mb-6 font-heading">
                Soforthilfe für <span className="text-olive-green">Gaza</span>.
              </h2>
              <div className="w-24 h-1 bg-olive-green mx-auto lg:mx-0 mb-6"></div>
              <p className="text-xl text-charcoal/80 leading-relaxed">
                Wir ermöglichen, was aktuell nur schwer gelingt: Wir bringen Hilfe dorthin, wo sie kaum ankommt - direkt, persönlich, über unsere Familie vor Ort. Da wir selbst betroffen sind, mussten wir über die letzten Jahre lernen, wie Unterstützung in Gaza wirklich ankommt - und können das Wissen und die Wege jetzt nutzen. So schaffen wir Hilfe, die verbindet. Hoffnung, die bleibt. Gemeinsam mit euch. Für mehr Menschlichkeit.
              </p>
            </div>

            {/* Logo Section - Rechts */}
            <div className="flex justify-center lg:justify-end">
              <div className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-lg border border-olive-green/10">
                <img
                  src="/images/hay-logo-updated.png"
                  alt="hay - humanity. action. you. Logo"
                  className="w-64 h-auto opacity-95"
                />
              </div>
            </div>
          </motion.div>

          {/* School Reconstruction Project */}
          <motion.div
            className="mb-20"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-charcoal font-heading mb-6">Unser aktuelles Projekt: <span className="text-olive-green">Wiederaufbau einer Schule.</span></h3>
              <div className="max-w-4xl mx-auto mb-8">
                <p className="text-lg text-charcoal/80 leading-relaxed">
                  Schulen sind Orte der Sicherheit, Bildung und Zukunft. 1995 hat unsere Familie eine Schule in Gaza mitaufgebaut. In den letzten zweieinhalb Jahren war sie nicht nur ein Lernort, sondern auch Zuflucht für Familien. 2025 wurde das Gebäude durch Bomben zerstört. Seit einigen Monaten bauen unsere Partner vor Ort die Schule mit unserer finanziellen Unterstützung wieder auf. Wir haben bereits große Fortschritte gemacht: Der Schulbetrieb kann aktuell wieder regelmäßig stattfinden. Die Kinder kommen zusammen, lernen gemeinsam, spielen miteinander und erhalten Mahlzeiten. Ein Anfang ist gemacht, aber der Wiederaufbau ist noch lange nicht abgeschlossen. Unter schwierigen Umständen machen wir weiter. Unsere Vision ist klar: 900 Kindern den Zugang zu Bildung und die Chance auf einen Alltag ermöglichen. Danke, dass du diesen Weg mit uns gehst!
                </p>
              </div>
            </div>
            
            {/* School Project Image Slider */}
            <div className="relative max-w-4xl mx-auto">
              <div className="overflow-hidden rounded-2xl shadow-2xl">
                <motion.div
                  className="flex"
                  animate={{ x: `${-currentSchoolSlide * 100}%` }}
                  transition={{ duration: 0.8, ease: "easeInOut" }}
                >
                  {schoolImages.map((image, index) => (
                    <div key={index} className="min-w-full relative">
                      <img
                        src={image.src}
                        alt={image.alt}
                        className={`w-full h-96 object-cover ${index === 4 ? 'object-bottom md:object-[center_80%]' : ''} ${index === 5 ? 'object-bottom md:object-[center_85%]' : ''}`}
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                        <p className="text-white text-lg font-medium">{image.description}</p>
                      </div>
                    </div>
                  ))}
                </motion.div>
              </div>
              
              {/* Slider Controls */}
              <div className="flex justify-center mt-6 space-x-2">
                {schoolImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSchoolSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentSchoolSlide ? 'bg-olive-green scale-125' : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
              
              {/* Navigation Arrows */}
              <button
                onClick={prevSchoolSlide}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={nextSchoolSlide}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </motion.div>

          {/* Gaza Reality Image Slider */}
          <motion.div
            className="mb-20"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-charcoal font-heading mb-6">Die Realität in Gaza.</h3>
              <div className="max-w-4xl mx-auto mb-8">
                <p className="text-lg text-charcoal/80 leading-relaxed">
                  Seit Beginn des Krieges hat sich das Leben im Gazastreifen dramatisch verändert. <a href="https://en.wikipedia.org/wiki/Casualties_of_the_Gaza_war" target="_blank" rel="noopener noreferrer" className="text-olive-green hover:text-olive-dark underline transition-colors">Fast 75.000 Menschen wurden getötet</a>, <a href="https://news.un.org/en/story/2025/10/1166023" target="_blank" rel="noopener noreferrer" className="text-olive-green hover:text-olive-dark underline transition-colors">über die Hälfte der Krankenhäuser ruiniert</a>, <a href="https://www.instagram.com/p/DPocU1fjJXm/?utm_source=ig_web_button_share_sheet&igsh=MzRlODBiNWFlZA%3D%3D" target="_blank" rel="noopener noreferrer" className="text-olive-green hover:text-olive-dark underline transition-colors">knapp 80 % der Gebäude wurden zerstört, über 90 % der Schulen sind beschädigt oder zerstört, über 98 % des Ackerlands ist beschädigt</a>. Die Vereinten Nationen sprechen von der schlimmsten Verwüstung einer Region seit 1945. Millionen Menschen wurden vertrieben und mussten mehrfach flüchten, oft ohne sicheren Zufluchtsort, ohne medizinische Versorgung, ohne Nahrung oder Trinkwasser. <a href="https://www.ohchr.org/sites/default/files/documents/hrbodies/hrcouncil/sessions-regular/session60/advance-version/a-hrc-60-crp-3.pdf" target="_blank" rel="noopener noreferrer" className="text-olive-green hover:text-olive-dark underline transition-colors">Nach Auffassung der unabhängigen Untersuchungskommission des UN-Menschenrechtsrats begeht Israel in Gaza einen Genozid</a>.
                </p>
              </div>
            </div>
            
            <div className="relative max-w-4xl mx-auto">
              <div className="overflow-hidden rounded-2xl shadow-2xl">
                <motion.div
                  className="flex"
                  animate={{ x: `${-currentSlide * 100}%` }}
                  transition={{ duration: 0.8, ease: "easeInOut" }}
                >
                  {gazaImages.map((image, index) => (
                    <div key={index} className="min-w-full relative">
                      <img
                        src={image.src}
                        alt={image.alt}
                        className="w-full h-96 object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                        <p className="text-white text-lg font-medium">{image.description}</p>
                      </div>
                    </div>
                  ))}
                </motion.div>
              </div>
              
              {/* Slider Controls */}
              <div className="flex justify-center mt-6 space-x-2">
                {gazaImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentSlide ? 'bg-olive-green scale-125' : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
              
              {/* Navigation Arrows */}
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </motion.div>

          {/* Wer wir sind - Personal Story */}
          <motion.div
            id="wer-wir-sind"
            className="mb-20"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="bg-white rounded-3xl shadow-2xl p-12 border border-olive-green/20">
              <h3 className="text-4xl font-bold text-charcoal font-heading mb-8 text-center">
                Wer wir sind.
              </h3>
              
              <div className="grid lg:grid-cols-2 gap-12 items-start">
                <div className="space-y-6">
                  <p className="text-lg text-charcoal/80 leading-relaxed">
                    Wir sind <strong>Stefan, Thomas, Daniel und Nadja</strong> – Geschwister, Kinder einer palästinensisch-deutschen Familie – und tief verbunden mit dem Leben und Leiden der Menschen in Gaza.
                  </p>
                  
                  {/* Team Member Slider */}
                  <div className="my-8">
                    <div className="relative max-w-[200px] mx-auto">
                      <div className="overflow-hidden rounded-xl shadow-lg">
                        <motion.div
                          className="flex"
                          animate={{ x: `${-currentPersonSlide * 100}%` }}
                          transition={{ duration: 0.6, ease: "easeInOut" }}
                        >
                          {teamMembers.map((member, index) => (
                            <div key={index} className="min-w-full">
                              <img
                                src={member.image}
                                alt={member.alt}
                                className="w-full h-auto object-contain"
                              />
                            </div>
                          ))}
                        </motion.div>
                      </div>
                      
                      {/* Slider Controls */}
                      <div className="flex justify-center mt-3 space-x-2">
                        {teamMembers.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setCurrentPersonSlide(index)}
                            className={`w-2 h-2 rounded-full transition-all ${
                              index === currentPersonSlide ? 'bg-olive-green scale-125' : 'bg-gray-300 hover:bg-gray-400'
                            }`}
                          />
                        ))}
                      </div>
                      
                      {/* Navigation Arrows */}
                      <button
                        onClick={prevPersonSlide}
                        className="absolute left-1 top-1/2 transform -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-1.5 rounded-full transition-all"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <button
                        onClick={nextPersonSlide}
                        className="absolute right-1 top-1/2 transform -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-1.5 rounded-full transition-all"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  <p className="text-lg text-charcoal/80 leading-relaxed">
                    Was uns antreibt, ist keine Theorie. Es sind die Stimmen, Gesichter und Geschichten unserer eigenen Familie: Tanten, Onkel, Cousinen und Cousins, die Tag für Tag ums Überleben kämpfen. Familienmitglieder, die getötet wurden. Menschen, deren Zuhause zerstört, deren Zukunft ohne Perspektive, deren Alltag geprägt ist von Angst, Hunger und Verlust.
                  </p>
                </div>

                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-olive-green to-olive-dark text-white p-6 rounded-xl">
                    {/* Tante Yusra Story - moved to top on desktop */}
                    <div className="bg-white/20 backdrop-blur-sm p-4 rounded-lg border-l-4 border-white/50 mb-6">
                      <p className="text-white/95 leading-relaxed italic">
                        Unsere Tante Yusra war Lehrerin mit Leib und Seele – ihre Schule liegt heute in Schutt und Asche. Unser Onkel Mustafa - einst voller Lebensfreude - liegt heute unter Trümmern begraben. Unsere Cousins und Cousinen, zusammen mit ihren Familien, früher so unbeschwert, erstarren heute bei jedem Geräusch in der Nacht. Ihre Geschichten sind keine Schlagzeilen. Sie sind unsere Realität.
                      </p>
                    </div>
                    <p className="text-xl font-bold text-center mb-3">
                      Wir können nicht wegsehen.
                    </p>
                    <p className="text-white/90 leading-relaxed mb-6">
                      Als Menschen, die zwischen zwei Welten leben – sicher in Deutschland, mit dem Herzen und voller Sorge in Gaza – spüren wir eine Verantwortung. Eine, die größer ist als nur unsere Familie. Aus dieser Verantwortung ist „hay - humanity. action. you. e.V." entstanden: Ein gemeinnütziger Verein für humanitäre Hilfe und Solidarität.
                    </p>
                    <div className="text-center">
                      <button
                        onClick={() => {
                          const element = document.getElementById('donate');
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        }}
                        className="bg-white text-olive-green hover:bg-cream px-8 py-3 rounded-full font-bold text-lg transition-all transform hover:scale-105 shadow-lg"
                      >
                        Jetzt spenden
                      </button>
                    </div>
                  </div>

                  <p className="text-lg text-charcoal/80 leading-relaxed">
                    Schon in der Vergangenheit haben wir gemeinsam geholfen – etwa während des Ukraine-Kriegs. Unsere Motivation bleibt stets dieselbe: Den Menschen helfen, die gerade niemanden haben. Dabei zählen keine Nationalitäten, keine politischen Haltungen. Es zählt Menschlichkeit.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Unsere Überzeugung - moved under "Wer wir sind" */}
          <motion.div
            className="mb-20"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-4xl font-bold text-charcoal text-center mb-12 font-heading">
              Unsere Überzeugung.
            </h3>
            <div className="grid md:grid-cols-3 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  className="bg-white p-8 rounded-2xl shadow-xl text-center group hover:shadow-2xl transition-all hover:scale-105"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                >
                  <div className="w-16 h-16 bg-olive-green/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-olive-green/20 transition-colors">
                    <value.icon className="w-8 h-8 text-olive-green" />
                  </div>
                  <h4 className="text-2xl font-bold text-charcoal mb-4 font-heading">{value.title}</h4>
                  <p 
                    className="text-charcoal/70 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: value.description }}
                  ></p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Achievements */}
          <motion.div
            id="achievements"
            className="mb-20"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-4xl font-bold text-charcoal text-center mb-12 font-heading">
              Was wir bereits erreicht haben.
            </h3>
            <div className="grid md:grid-cols-3 gap-8">
              {achievements.map((achievement, index) => 
                achievement.link ? (
                  <motion.a
                    key={achievement.number}
                    href={achievement.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block bg-white p-8 rounded-2xl shadow-xl text-center group hover:shadow-2xl transition-all hover:scale-105 cursor-pointer"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    viewport={{ once: true }}
                  >
                    <div className="w-16 h-16 bg-olive-green/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-olive-green/20 transition-colors">
                      <achievement.icon className="w-8 h-8 text-olive-green" />
                    </div>
                    <div className="text-3xl font-bold text-olive-green mb-3">{achievement.number}</div>
                    <p className="text-charcoal/80 leading-relaxed text-sm">{achievement.text}</p>
                    <p className="text-olive-green text-xs mt-2 font-semibold">→ Zur GoFundMe-Kampagne</p>
                  </motion.a>
                ) : (
                  <motion.div
                    key={achievement.number}
                    className="bg-white p-8 rounded-2xl shadow-xl text-center group hover:shadow-2xl transition-all hover:scale-105"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    viewport={{ once: true }}
                  >
                    <div className="w-16 h-16 bg-olive-green/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-olive-green/20 transition-colors">
                      <achievement.icon className="w-8 h-8 text-olive-green" />
                    </div>
                    <div className="text-3xl font-bold text-olive-green mb-3">{achievement.number}</div>
                    <p className="text-charcoal/80 leading-relaxed text-sm">{achievement.text}</p>
                  </motion.div>
                )
              )}
            </div>
          </motion.div>



          {/* Wie wir arbeiten */}
          <motion.div
            id="wie-wir-helfen"
            className="mb-20"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-br from-olive-green to-olive-dark rounded-3xl p-12 text-white">
              <div className="text-center mb-8">
                <h3 className="text-4xl font-bold font-heading mb-6">
                  Wie wir helfen.
                </h3>
              </div>
              
              <div className="grid lg:grid-cols-2 gap-8 items-center mb-12">
                <div className="space-y-6">
                  <p className="text-xl text-white/90 leading-relaxed">
                    Wir arbeiten eng mit Personen vor Ort in Gaza und in Ägypten zusammen, die wir <strong>persönlich kennen und denen wir vertrauen</strong>.
                  </p>
                  <p className="text-lg text-white/90 leading-relaxed">
                    Auch wir als Familie haben bereits zehntausende Euro aus eigenen Mitteln gespendet und deren zielgerichtete Verwendung begleitet – vom Fluchthilfe-Fall bis zur Versorgung ganzer Familien. So stellen wir sicher, dass jede Hilfe genau dort ankommt, wo sie am dringendsten gebraucht wird.
                  </p>
                </div>
                
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-8">
                  <h4 className="text-2xl font-bold mb-6 text-center">Bisherige Hilfsmaßnahmen.</h4>
                  <div className="space-y-4 mb-8">
                    {helpMeasures.map((measure, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-white flex-shrink-0 mt-1" />
                        <p className="text-white/90 leading-relaxed text-sm">{measure}</p>
                      </div>
                    ))}
                  </div>
                  <div className="text-center">
                    <button
                      onClick={() => {
                        const element = document.getElementById('donate');
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }}
                      className="bg-white text-olive-green hover:bg-cream px-8 py-4 rounded-full font-bold text-lg transition-all transform hover:scale-105 shadow-xl"
                    >
                      Jetzt spenden
                    </button>
                  </div>
                </div>
              </div>

              {/* Three Goals moved from "Was wir tun" */}
              <div className="grid lg:grid-cols-3 gap-8">
                <motion.div
                  className="bg-white/20 backdrop-blur-sm rounded-2xl p-8 group hover:bg-white/30 transition-all hover:scale-105"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-white/30 transition-colors">
                    <Heart className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="text-2xl font-bold text-white mb-4 font-heading text-center">1. Soforthilfe</h4>
                  <p className="text-white/90 leading-relaxed text-center">
                    Wir stellen die Gelder für notwendige Hilfsgüter bereit – Wasser, Nahrung, Medikamente. Über direkte Kontakte vor Ort können wir die Hilfe genau dort leisten, wo sie am dringendsten gebraucht wird.
                  </p>
                </motion.div>

                <motion.div
                  className="bg-white/20 backdrop-blur-sm rounded-2xl p-8 group hover:bg-white/30 transition-all hover:scale-105"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-white/30 transition-colors">
                    <MapPin className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="text-2xl font-bold text-white mb-4 font-heading text-center">2. Fluchthilfe</h4>
                  <p className="text-white/90 leading-relaxed text-center">
                    Aktuell ist eine Flucht leider nicht möglich. Sollte dies wieder realisierbar sein, unterstützen wir Menschen, die aus dem Kriegsgebiet fliehen wollen – durch die Finanzierung von Transporten oder notwendigen Dokumenten.
                  </p>
                </motion.div>

                <motion.div
                  className="bg-white/20 backdrop-blur-sm rounded-2xl p-8 group hover:bg-white/30 transition-all hover:scale-105"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-white/30 transition-colors">
                    <Target className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="text-2xl font-bold text-white mb-4 font-heading text-center">3. Neuanfang & Wiederaufbau</h4>
                  <p className="text-white/90 leading-relaxed text-center">
                    Für Menschen, die fliehen konnten – und für jene, die in Gaza bleiben (müssen) – wollen wir Perspektiven schaffen: Notunterkünfte, Bildungsangebote, Arbeit, langfristige Hilfe.
                  </p>
                </motion.div>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default About;